/**
 * @fileoverview Eurofarma Stock Page
 * Displays stock inventory across ALL institutions (for EUROFARMA_GESTAO_LENA profile)
 */

import React, { useState, useCallback, useRef, useEffect, useContext } from "react";
import { formatDate } from "../../utils/formatters";
import { Typography, Chip, Box, Grid, Paper } from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import {
    DmsPage,
    DmsTable,
    DmsAltBox,
    intl,
    fetchData,
    HTTP_STATUS,
    LanguageBaseContext,
} from "dmsbasereact";
import { ReportFilters } from "../ReportsPage/components/ReportFilters";
import { DmsInstitutionSelect } from "../../components/DmsInstitutionSelect/DmsInstitutionSelect";
import { isExpired, isExpiringSoon } from "../../utils/expiration";

const getExpirationChip = (expirationDate) => {
    if (!expirationDate) {
        return <Chip label={intl.get("NoExpiration").d("No Expiration")} size="small" />;
    }
    if (isExpired(expirationDate)) {
        return <Chip label={intl.get("Expired").d("Expired")} color="error" size="small" />;
    }
    if (isExpiringSoon(expirationDate, 180)) {
        return <Chip label={intl.get("ExpiringSoon").d("Expiring Soon")} color="warning" size="small" />;
    }
    return <Chip label={intl.get("Valid").d("Valid")} color="success" size="small" />;
};

export const StockManagementPage = () => {
    const tableRef = useRef(null);
    const debounceRef = useRef(null);
    const { language } = useContext(LanguageBaseContext);

    const [stats, setStats] = useState({
        total_batches: 0,
        total_quantity: 0,
        expired_count: 0,
        expiring_soon_count: 0
    });

    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});
    const [filterFields, setFilterFields] = useState([]);
    const [selectedInstitutionPk, setSelectedInstitutionPk] = useState(null);

    // Fetch filter options (CD list)
    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                const response = await fetchData("/batch_location/management/cd-options", "GET", true);
                if (response.status === HTTP_STATUS.OK_200 && response.data?.data) {
                    const cdOptions = response.data.data.map((cd) => ({ value: cd, label: cd }));
                    const statusOptions = [
                        { value: "valid", label: intl.get("Valid").d("Valid") },
                        { value: "expiring_soon", label: intl.get("ExpiringSoon").d("Expiring Soon") },
                        { value: "expired", label: intl.get("Expired").d("Expired") },
                    ];
                    setFilterFields([
                        { name: "cd", label: intl.get("DistributionCenter").d("Distribution Center"), type: "multiselect", options: cdOptions },
                        { name: "status", label: intl.get("Status").d("Status"), type: "select", options: statusOptions },
                        { name: "batch_number", label: intl.get("Batch").d("Batch"), type: "text" },
                        { name: "part_name", label: intl.get("Product").d("Product"), type: "text" },
                        { name: "part_number", label: intl.get("ProductCode").d("Product Code"), type: "text" },
                    ]);
                }
            } catch (err) {
                console.error("Error fetching filter options:", err);
            }
        };
        loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- language is required for i18n updates
    }, [language]);

    const fetchStats = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (selectedInstitutionPk) params.append("organization_branch_pk", selectedInstitutionPk);
            const cdValue = Array.isArray(appliedFilters.cd) ? appliedFilters.cd.join(",") : appliedFilters.cd;
            if (cdValue) params.append("cd", cdValue);
            if (appliedFilters.batch_number) params.append("batch_number", appliedFilters.batch_number);
            if (appliedFilters.part_name) params.append("part_name", appliedFilters.part_name);
            if (appliedFilters.part_number) params.append("part_number", appliedFilters.part_number);
            if (appliedFilters.status) params.append("status", appliedFilters.status);
            const qs = params.toString();
            const url = qs
                ? `/batch_location/management/stats?${qs}`
                : "/batch_location/management/stats";
            const response = await fetchData(url, "GET", true);
            if (response.status === HTTP_STATUS.OK_200 && response.data?.data) {
                setStats(response.data.data);
            }
        } catch (err) {
            console.error("Error fetching stats:", err);
        }
    }, [appliedFilters, selectedInstitutionPk]);

    const buildPreFilter = useCallback(() => {
        const preFilters = [];
        if (selectedInstitutionPk) preFilters.push({ name: "organization_branch_pk", value: selectedInstitutionPk });
        const cdValue = Array.isArray(appliedFilters.cd) ? appliedFilters.cd.join(",") : appliedFilters.cd;
        if (cdValue) preFilters.push({ name: "cd", value: cdValue });
        if (appliedFilters.batch_number) preFilters.push({ name: "batch_number", value: appliedFilters.batch_number });
        if (appliedFilters.part_name) preFilters.push({ name: "part_name", value: appliedFilters.part_name });
        if (appliedFilters.part_number) preFilters.push({ name: "part_number", value: appliedFilters.part_number });
        if (appliedFilters.status) preFilters.push({ name: "status", value: appliedFilters.status });
        return preFilters;
    }, [appliedFilters, selectedInstitutionPk]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const updateTableAndStats = useCallback(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            fetchStats();
            tableRef.current?.updateTable();
        }, 500);
    }, [fetchStats]);

    useEffect(() => {
        updateTableAndStats();
    }, [appliedFilters, updateTableAndStats]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    const handleInstitutionChange = useCallback((value) => {
        setSelectedInstitutionPk(value || null);
    }, []);

    const handleRefresh = useCallback(() => {
        fetchStats();
        tableRef.current?.updateTable();
    }, [fetchStats]);

    const handleApplyFilters = useCallback(() => {
        setAppliedFilters({ ...filters });
    }, [filters]);

    const handleClearFilters = useCallback(() => {
        setFilters({});
        setAppliedFilters({});
    }, []);

    const header = [
        {
            label: intl.get("Batch").d("Batch"),
            column_name: "batch_number",
        },
        {
            label: intl.get("Product").d("Product"),
            column_name: "part_name",
        },
        {
            label: intl.get("ProductCode").d("Product Code"),
            column_name: "part_number",
        },
        {
            label: intl.get("Institution").d("Institution"),
            column_name: "organization_name",
        },
        {
            label: intl.get("DistributionCenter").d("CD"),
            column_name: "cd",
            element: (row) => row.cd || "-",
        },
        {
            label: intl.get("Quantity").d("Quantity"),
            column_name: "quantity",
            element: (row) => (
                <Typography variant="body2" fontWeight="bold">
                    {row.quantity || 0}
                </Typography>
            ),
        },
        {
            label: intl.get("Expiration").d("Expiration"),
            column_name: "expiration",
            type: "date",
            element: (row) => formatDate(row.expiration),
        },
        {
            label: intl.get("Status").d("Status"),
            column_name: "status",
            sortable: false,
            element: (row) => getExpirationChip(row.expiration),
        },
    ];

    return (
        <DmsPage name="EurofarmaStock">
            <DmsAltBox.Card>
                <DmsAltBox.Header
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor: "primary.main",
                        color: "white",
                        px: 2,
                        py: 1,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1}>
                        <InventoryIcon sx={{ color: "white" }} />
                        <Typography variant="h6" sx={{ color: "white", fontWeight: 600 }}>
                            {intl.get("EurofarmaStock").d("Eurofarma Stock")}
                        </Typography>
                    </Box>
                    <ReportFilters
                        filterFields={filterFields}
                        filters={filters}
                        onFiltersChange={setFilters}
                        onApply={handleApplyFilters}
                        onClear={handleClearFilters}
                        onRefresh={handleRefresh}
                        showDateRange={false}
                    />
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <Box sx={{ mb: 2, maxWidth: 400 }}>
                        <DmsInstitutionSelect
                            label={intl.get("FilterByInstitution").d("Filter by Institution")}
                            value={selectedInstitutionPk || ""}
                            onChange={handleInstitutionChange}
                            requireApproved={true}
                            needsAuthentication={true}
                        />
                    </Box>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    textAlign: "center"
                                }}
                            >
                                <Typography variant="h4" color="primary.main" fontWeight="bold">
                                    {stats.total_batches}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {intl.get("TotalBatches").d("Total Batches")}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    textAlign: "center"
                                }}
                            >
                                <Typography variant="h4" color="info.main" fontWeight="bold">
                                    {stats.total_quantity}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {intl.get("TotalUnits").d("Total Units")}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: stats.expiring_soon_count > 0 ? "warning.main" : "divider",
                                    borderRadius: 2,
                                    textAlign: "center"
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    color={stats.expiring_soon_count > 0 ? "warning.main" : "textSecondary"}
                                    fontWeight="bold"
                                >
                                    {stats.expiring_soon_count}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {intl.get("ExpiringSoon").d("Expiring Soon")}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: stats.expired_count > 0 ? "error.main" : "divider",
                                    borderRadius: 2,
                                    textAlign: "center"
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    color={stats.expired_count > 0 ? "error.main" : "textSecondary"}
                                    fontWeight="bold"
                                >
                                    {stats.expired_count}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {intl.get("Expired").d("Expired")}
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

<DmsTable
                        ref={tableRef}
                        header={header}
                        url="/batch_location/management/table"
                        managed={true}
                        needs_authentication={true}
                        export_table={false}
                        pre_filter={buildPreFilter()}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>
        </DmsPage>
    );
};

StockManagementPage.propTypes = {};
